/**
 * Keeps swearing and slurs off the leaderboard, in the languages the game's
 * players are likely to use (English, Spanish, Italian, a little French and
 * German). Shared by the game (it says no before sending) and the server (it
 * refuses to store, and masks anything older when showing a board).
 *
 * Names are normalized first: lower case, accents stripped, leetspeak undone
 * (sh1t, $h!t, 4ss), separators dropped (f.u.c.k, d i c k), stretched letters
 * collapsed (fuuuck). Then two kinds of term:
 * - STRONG terms are caught anywhere in the name, even inside other words.
 * - WORD terms are too short or too common inside innocent words (Dickens,
 *   Cassandra, computadora, Nazionale, Scunthorpe): only as a whole word,
 *   give or take a plural or an -er/-ed/-ing.
 */

const STRONG = [
  // English
  'fuck', 'fcuk', 'shit', 'bitch', 'whore', 'nigger', 'nigga', 'niggaz', 'faggot', 'dickhead', 'cocksuck', 'motherf',
  'pussy', 'jizz', 'porn', 'hitler', 'asshole', 'arsehole', 'bollock', 'wetback', 'shithead', 'slut', 'penis', 'vagina', 'dildo',
  // A racial slur in Spanish and Italian (negroide) and English (negroid). Not plain "negro": in Spanish it's
  // also an everyday nickname ("el Negro").
  'negroid',
  // Spanish (Colombian favourites included)
  'hijueputa', 'hijodeputa', 'hijoeputa', 'malparid', 'gonorrea', 'pendej', 'mierda', 'cabron', 'maricon', 'pirob', 'culer',
  'chupamel', 'chupapija', 'mamaguev', 'mamahuev', 'carechimba', 'gilipolla', 'follar', 'cojones', 'putamadre',
  // Italian
  'cazz', 'stronz', 'vaffancul', 'fancul', 'puttan', 'minchi', 'coglion', 'bastard', 'frocio', 'froci', 'merda', 'porcodio',
  'diocane', 'porcamadonna', 'troiona',
  // French, German
  'putain', 'salope', 'encule', 'connard', 'fotze', 'hurensohn', 'scheisse', 'wichser',
];

const WORD = [
  // English
  'ass', 'arse', 'cunt', 'twat', 'dick', 'cock', 'tit', 'tits', 'boob', 'anal', 'anus', 'rape', 'rapist', 'nazi', 'kkk', 'sex',
  'cum', 'fag', 'dyke', 'spic', 'chink', 'kike', 'tranny', 'retard', 'wank', 'wanker', 'prick', 'hoe', 'nig', 'fuk',
  // Spanish
  'puta', 'puto', 'culo', 'verga', 'pinga', 'zorra', 'marica', 'huevon', 'guevon', 'weon', 'hdp', 'coño', 'chucha',
  // Italian
  'troia', 'figa', 'fica', 'culone',
];

/** Endings a WORD term may carry and still count: plurals, -er, -ed, -ing (not -y or -o: Spicy and Tito are fine). */
const SUFFIXES = ['', 's', 'es', 'er', 'ers', 'ed', 'ing'];

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '!': 'i', '|': 'i', '3': 'e', '4': 'a', '@': 'a', '5': 's', $: 's', '7': 't', '+': 't', '8': 'b', '9': 'g' };

/** Combining accent marks (U+0300 to U+036F), stripped after NFKD splits them off their letters. */
const MARKS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

/** Lower case, accents off, camelCase split ("BigDick"); with `leet`, look-alike digits and symbols become letters. */
function normalize(s: string, leet = true): string {
  const plain = s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .normalize('NFKD')
    .replace(MARKS, '')
    .toLowerCase();
  return (leet ? Array.from(plain).map((ch) => LEET[ch] ?? ch).join('') : plain).replace(/ph/g, 'f');
}

const strip = (s: string): string => s.replace(/[^a-z]/g, '');
const collapse = (s: string): string => s.replace(/([a-z])\1+/g, '$1');

const wordHit = (w: string): boolean =>
  WORD.some((term) => {
    const t = strip(normalize(term));
    return w.startsWith(t) && SUFFIXES.includes(w.slice(t.length));
  });

/** True if the name carries a swear word or a slur. */
export function isProfane(name: string): boolean {
  const n = normalize(name);
  const compact = strip(n);
  if (!compact) return false;
  for (const form of [compact, collapse(compact)]) {
    if (STRONG.some((t) => form.includes(t))) return true;
  }
  // Words both ways: with look-alikes read as letters (sh1t), and with digits as gaps (Dick99).
  const words = [...n.split(/[^a-z]+/), ...normalize(name, false).split(/[^a-z]+/)].filter(Boolean);
  for (const w of words) if (wordHit(w) || wordHit(collapse(w))) return true;
  // Spelled out with gaps between the letters ("d i c k", "a.s.s"): the whole name as one word.
  const letters = n.split(/[^a-z]+/).filter(Boolean);
  if (letters.length > 1 && letters.every((w) => w.length <= 2) && (wordHit(compact) || wordHit(collapse(compact)))) return true;
  return false;
}

/** What a board shows for a name: the name, or a neutral stand-in if it shouldn't be seen. */
export function displayName(name: string): string {
  return isProfane(name) ? 'Anonymous driver' : name;
}
